import React, { useRef, useState } from "react";
import { Card, CardBody, Row, Col, Input, Label, FormGroup } from "reactstrap";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { StepComponentProps } from "../types/proposalForm.types";
import { uploadFileToS3Kyc } from "services/kycService";
import { addLicDocumentAdminThunk, deleteLicDocumentAdminThunk } from "slices/KYC/thunk";

const DOC_TYPE_ID_STEP3 = 1 as const;

const Step3Identification: React.FC<StepComponentProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  licId,
}) => {
  const dispatch = useDispatch();
  const [uploadingField, setUploadingField] = useState<"passport_copy" | "emirates_id_copy" | null>(null);
  const passportFileRef = useRef<HTMLInputElement>(null);
  const emiratesIdFileRef = useRef<HTMLInputElement>(null);

  const renderInput = (
    name: keyof typeof values,
    label: string,
    type: "text" | "number" | "date" | "email" | "tel" = "text",
    placeholder?: string,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <Input
        type={type}
        name={name as string}
        value={String(values[name] || "")}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={!!(touched[name] && errors[name])}
        placeholder={placeholder}
        className="form-control"
      />
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  const renderDateInput = (
    name: keyof typeof values,
    label: string,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <div className="date-input-wrapper">
        <Input
          type="text"
          name={name as string}
          value={String(values[name] || "")}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={!!(touched[name] && errors[name])}
          className="form-control"
          placeholder="DD/MM/YYYY"
          id={`date-${name}`}
          onFocus={(e) => {
            e.target.type = "date";
          }}
        />
        <i 
          className="ri-calendar-line calendar-icon"
          onClick={() => {
            const input = document.getElementById(`date-${name}`) as HTMLInputElement;
            if (input) {
              input.type = "date";
              if (input.showPicker) {
                input.showPicker();
              } else {
                input.click();
              }
            }
          }}
        />
      </div>
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  const renderSelect = (
    name: keyof typeof values,
    label: string,
    options: Array<{ value: string; label: string }>,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <Input
        type="select"
        name={name as string}
        value={String(values[name] || "")}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={!!(touched[name] && errors[name])}
        className="form-control"
      >
        <option value="">Select visa type</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Input>
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: "passport_copy" | "emirates_id_copy"
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error("File size should not exceed 5MB");
      return;
    }
    const validTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a PNG, JPG, PDF, or DOCX file");
      return;
    }

    if (!licId) {
      toast.error("Please complete Step 2 (Personal Info) first to get LIC id.");
      return;
    }

    setUploadingField(fieldName);
    try {
      const document_url = await uploadFileToS3Kyc(file);
      setFieldValue(fieldName, document_url);

      const doc_typeid = DOC_TYPE_ID_STEP3;
      const documentName = fieldName === "passport_copy" ? "Passport Copy" : "Emirates ID";
      const result = await dispatch(
        addLicDocumentAdminThunk({
          doc_typeid,
          lic_id: licId,
          document_url,
          document_name: documentName,
          note: "",
        }) as any
      );
      if (result?.success && result?.document_id != null) {
        const docIdField = fieldName === "passport_copy" ? "passport_copy_document_id" : "emirates_id_copy_document_id";
        setFieldValue(docIdField, result.document_id);
        toast.success(fieldName === "passport_copy" ? "Passport copy uploaded." : "Emirates ID copy uploaded.");
      } else if (!result?.success) {
        toast.error(result?.message ?? "Failed to save document.");
      }
    } catch (err: any) {
      toast.error(err?.message ?? "Upload failed.");
    } finally {
      setUploadingField(null);
      e.target.value = "";
    }
  };

  const renderFileUpload = (
    fieldName: "passport_copy" | "emirates_id_copy",
    label: string,
    uploadText: string,
    fileRef: React.RefObject<HTMLInputElement>,
    required: boolean = false
  ) => {
    const fileValue = values[fieldName];
    const docIdField = fieldName === "passport_copy" ? "passport_copy_document_id" : "emirates_id_copy_document_id";
    const documentId = values[docIdField] as number | null | undefined;
    const hasFile = !!fileValue;

    const handleRemove = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (documentId != null) {
        try {
          const result = await dispatch(deleteLicDocumentAdminThunk(documentId) as any);
          if (!result?.success) {
            toast.error(result?.message ?? "Failed to delete document.");
            return;
          }
          toast.success("Document removed.");
        } catch (err: any) {
          toast.error(err?.message ?? "Failed to delete document.");
          return;
        }
      }
      setFieldValue(fieldName, null);
      setFieldValue(docIdField, null);
      if (fileRef.current) fileRef.current.value = "";
    };

    return (
      <FormGroup className="form-group">
        <Label>
          {label}
          {required && <span className="text-danger">*</span>}
        </Label>
        <div
          className={`file-upload-area ${hasFile ? "has-file" : ""} ${uploadingField === fieldName ? "uploading" : ""}`}
          onClick={() => !uploadingField && fileRef.current?.click()}
        >
          {uploadingField === fieldName ? (
            <p className="upload-text">Uploading...</p>
          ) : hasFile && typeof fileValue === "string" ? (
            <div className="file-preview">
              <a
                href={fileValue}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="document-preview-link"
                onClick={(e) => e.stopPropagation()}
                title="Click to download"
              >
                {/\.(png|jpe?g|webp)(\?|$)/i.test(fileValue) ? (
                  <img src={fileValue} alt="Document preview" className="document-preview-img" />
                ) : (
                  <span className="document-preview-placeholder">
                    <i className="ri-file-text-line" />
                    <span>{/\.pdf(\?|$)/i.test(fileValue) ? "PDF" : /\.docx?(\?|$)/i.test(fileValue) ? "DOCX" : "Document"}</span>
                  </span>
                )}
              </a>
              <button
                type="button"
                className="remove-file-btn"
                onClick={handleRemove}
                title="Delete document"
                aria-label="Delete document"
              >
                <i className="ri-close-line" aria-hidden />
              </button>
            </div>
          ) : (
            <>
              <i className="ri-upload-cloud-line upload-icon"></i>
              <p className="upload-text">{uploadText}</p>
              <p className="upload-hint">PNG, JPG, PDF or DOCX (Max 5MB)</p>
            </>
          )}
        </div>
        <input
          type="file"
          ref={fileRef}
          accept=".png,.jpg,.jpeg,.pdf,.docx"
          style={{ display: "none" }}
          onChange={(e) => handleFileChange(e, fieldName)}
        />
        {touched[fieldName] && errors[fieldName] && (
          <div className="invalid-feedback">{errors[fieldName]}</div>
        )}
      </FormGroup>
    );
  };

  return (
    <Card className="form-card shadow-sm border-0">
      <CardBody>
        <h5 className="form-title">Identification Details</h5>
        <p className="form-subtitle">
          Please provide your valid identification documents
        </p>

        {/* Row 1: Passport Information */}
        <Row>
          <Col md={4}>
            {renderInput("passsportnumber", "Passport Number", "text", "XXX")}
          </Col>
          <Col md={4}>
            {renderDateInput("passport_doe", "Date of Expiry")}
          </Col>
          <Col md={4}>
            {renderInput(
              "countryofissue",
              "Country of Issue",
              "text",
              "XXX"
            )}
          </Col>
        </Row>

        {/* Row 2: CPR/Emirate ID/Citizenship Card Information */}
        <Row>
          <Col md={6}>
            {renderInput(
              "citizenshipnumber",
              "CPR/ Emirate ID/ Citizenship Card No.",
              "text",
              "e.g., 784-1234-1234567-1"
            )}
          </Col>
          <Col md={6}>
            {renderDateInput(
              "citizenship_doe",
              "Date of Expiry"
            )}
          </Col>
        </Row>

        {/* Row 3: Visa Information */}
        <Row>
          <Col md={6}>
            {renderInput(
              "visaissue_country",
              "Visa Issued by Country/ Emirates*",
              "text",
              undefined,
              true
            )}
          </Col>
          <Col md={6}>
            {renderDateInput("visa_doe", "Date of Expiry")}
          </Col>
        </Row>

        {/* Documents Upload Section */}
        <div className="documents-upload-section">
          <h6 className="documents-upload-title">
            Documents Upload (Upload clear copy of documents)
            <span className="text-danger">*</span>
          </h6>
          <div className="upload-fields-container">
            {renderFileUpload(
              "passport_copy",
              "Passport Copy",
              "Click to upload passport copy",
              passportFileRef,
              true
            )}
            {renderFileUpload(
              "emirates_id_copy",
              "Emirates ID/ National ID Proof",
              "Click to upload National ID proof",
              emiratesIdFileRef,
              true
            )}
          </div>
        </div>

        {/* Document Requirements Section */}
        <div className="document-requirements-section">
          <div className="requirements-content">
            <i className="ri-file-list-3-line requirements-icon"></i>
            <div className="requirements-text">
              <h6 className="requirements-title">Document Requirements</h6>
              <ul className="requirements-list">
                <li>Document must be valid and not expired.</li>
                <li>Scans should be clear and all text must be readable.</li>
                <li>File size should not exceed 5MB per document.</li>
                <li>Accepted formats: PDF, JPG, PNG, DOCX</li>
              </ul>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default Step3Identification;
